import { useTranslation } from 'react-i18next';
import './PurchasePricesUploader.scss';
import { ReactComponent as UploadIcon } from '../../icons/upload_arrow.svg';
import { ReactComponent as SendIcon } from '../../icons/sent.svg';
import FileUploader from '../../components/FileUploader/FileUploader';
import MiniLoader from '../../components/MiniLoader/MiniLoader';
import { downloadFile, formatDate } from '../../core/helpers';
import ManualUploadFile from '../../components/ManualDataUploader/ManualUploadFile';
import { useAppDispatch, useAppSelector } from '../../hooks';
import classNames from 'classnames';
import ManualUploadFileLink from '../../components/ManualDataUploader/ManualUploadFileLink';
import { useMemo, useState } from 'react';
import { addNotification } from '../../core/store/notificationsSlice';
import { postPurchasePricesTemplate } from '../../core/api';
import { DEFAULT_ERROR } from '../../components/Notifications/Notification';
import { format, subMonths } from 'date-fns';
import { Loader } from '../../components/Loader/Loader';
import { usePP } from '../../context/pp-context';
import { ACCEPTED_EXTS } from '../../context/useManualUpload';
import Button from '../../components/Button';
import TopProgressLoader from '../../components/TopProgressLoader';
import { ReactComponent as TipIcon } from '../../icons/tip-green-circle.svg';
import { ReactComponent as XLSIcon } from '../../icons/xls.svg';

export interface PurchasePricesUploadedFile {
  bucket: string;
  file: string;
  id: number;
  imported_at: string;
  rows_number: number;
  size: number;
  status: number;
  updated_products_number: number;
  validation_errors: {
    code: number;
    id: number;
    line: number;
  }[];
}

export enum FileStatusEnum {
  CREATED = 0,
  IMPORTED,
  FAILED,
}

export default function PurchasePricesUploader() {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const activeCompanyId = useAppSelector(
    (s) => s.user.activeCompany?.company_id
  );
  const [isLoadingFile, setIsLoadingFile] = useState(false);

  const {
    PPFiles,
    onSubmitPP,
    isUploadingPP,
    fileToUploadPP,
    onSelectPPFile,
    countPP,
    deletePPFileToUpload,
    onSubmitPPPrevPeriod,
    isLoadingPP,
  } = usePP();

  const deadlineDate = new Date();
  deadlineDate.setDate(10);

  const date = useMemo(() => {
    const d = new Date();
    return subMonths(d, 1);
  }, []);

  const today = new Date();

  const showUploaded =
    fileToUploadPP || PPFiles.some((f) => f.status === FileStatusEnum.CREATED);

  const isProcessing =
    isUploadingPP || PPFiles.some((f) => f.status === FileStatusEnum.CREATED);

  const renderContent = () => {
    if (!!countPP) {
      return (
        <>
          <div
            style={{
              lineHeight: 1,
            }}
          >
            <div className='PurchasePricesUploader__badge text-uppercase px-1 Text--sm text-white rounded d-inline-block py-0 m-0'>
              <span>{t('Until')}&nbsp;</span>
              {i18n.language === 'de' ? (
                <span>{formatDate(deadlineDate, 'do MMMM')}</span>
              ) : (
                <span>
                  {formatDate(deadlineDate, 'do MMMM').replace('th', 'th of')}
                </span>
              )}
            </div>
          </div>
          <div className='Text--30 fw-900 my-2'>
            {t('Update purchase prices')}
          </div>
          <div
            className='mb-2 multiline'
            style={{
              maxWidth: 650,
            }}
          >
            {t(
              'Download the list of all products with missing Purchase prices, fill the prices and upload the file back. You can upload files with prices not listed in the attachment.'
            )}
          </div>
          {!!PPFiles.length &&
            !PPFiles.some((f) => f.status === FileStatusEnum.CREATED) &&
            !!countPP && (
              <div className='PurchasePricesUploader__alert px-3 py-2 mb-4 text-center'>
                {t(
                  '*number* prices still missing. Please download here the updated list of products with missing Purchase prices'
                ).replace('*number*', String(countPP))}
              </div>
            )}
          <ManualUploadFileLink
            className='mb-3 FileLink--ManualDataUploader'
            bucket=''
            path=''
            name={`${countPP || 0}_missing_purchase_prices.xlsx`}
            icon={<XLSIcon />}
            onDownload={() => {
              setIsLoadingFile(true);
              postPurchasePricesTemplate(activeCompanyId)
                .then((res) => {
                  downloadFile(res, `${countPP || 0}_missing_purchase_prices`);
                })
                .catch(() => {
                  dispatch(
                    addNotification({
                      type: 'error',
                      text: t(DEFAULT_ERROR),
                    })
                  );
                })
                .finally(() => {
                  setIsLoadingFile(false);
                });
            }}
            isLoading={isLoadingFile}
          />
          <form
            onSubmit={onSubmitPP}
            className={classNames({
              'pt-3 border-top': showUploaded,
            })}
          >
            {showUploaded && (
              <>
                <div className='fw-bold mb-2'>{t('Uploaded files')}</div>
                <>
                  {PPFiles.sort((a, b) => {
                    if (!a.imported_at) return 1;
                    if (!b.imported_at) return -1;
                    return (
                      new Date(a.imported_at).getTime() -
                      new Date(b.imported_at).getTime()
                    );
                  })
                    .filter((f) => f.status === FileStatusEnum.CREATED)
                    .map((f) => {
                      const parts = f.file.split('/');
                      return (
                        <ManualUploadFileLink
                          isError={f.status === FileStatusEnum.FAILED}
                          key={f.file}
                          className='mb-3 FileLink--PurchasePrices'
                          bucket={f.bucket}
                          path={f.file}
                          name={parts[parts.length - 1]}
                          isLoading={f.status === FileStatusEnum.CREATED}
                          description={
                            f.imported_at ? (
                              <div className='d-flex align-items-center Text--sm Text--gray'>
                                <span>{t('sent on')}&nbsp;</span>
                                <span>
                                  {format(
                                    new Date(f.imported_at),
                                    'dd.MM.yyyy HH:mm'
                                  )}
                                </span>
                              </div>
                            ) : undefined
                          }
                        />
                      );
                    })}
                </>
              </>
            )}
            {fileToUploadPP && (
              <ManualUploadFile
                onDeleteFile={deletePPFileToUpload}
                file={fileToUploadPP}
                disabled={false}
                className='mb-3'
              />
            )}
            {fileToUploadPP ||
            PPFiles.some((f) => f.status === FileStatusEnum.CREATED) ? (
              <div className='pt-2'>
                <Button
                  className='py-2 ps-3 pe-4 d-flex align-items-center'
                  type='submit'
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <MiniLoader className='MiniLoader--sm d-flex align-items-center justify-content-center me-2 MiniLoader--gray' />
                  ) : (
                    <SendIcon className='me-2' />
                  )}
                  <span>{t('Send')}</span>
                </Button>
              </div>
            ) : (
              <div
                className={classNames(
                  'row row-cols-auto gx-3 align-items-center',
                  {
                    'pt-2': fileToUploadPP,
                  }
                )}
              >
                <div className='col'>
                  <FileUploader
                    onSelect={onSelectPPFile}
                    name='file'
                    accept={ACCEPTED_EXTS}
                    className='cursor-pointer Button--outline btn ps-3 pe-4 py-2 d-flex align-items-center'
                    multiple={false}
                  >
                    <UploadIcon className='me-2' />
                    <span>{t('Select file')}</span>
                  </FileUploader>
                </div>
                <div className='col Text--gray Text--sm'>
                  File format: CSV, XLS, XLSX
                </div>
              </div>
            )}
          </form>
        </>
      );
    }

    return (
      <div
        style={{
          maxWidth: 620,
        }}
      >
        <div className='Text--30 fw-900 mb-2'>
          {today.getDate() < 5
            ? t(
                'If you have any missing purchase prices, they will appear here on *month* 5th'
              ).replace('*month*', formatDate(today, 'MMMM'))
            : t('For *month*, purchase prices are complete').replace(
                '*month*',
                formatDate(date, 'MMMM yyyy')
              )}
        </div>
        <div className='mb-2'>
          {t(
            'But if you have received request by email from the manager for past dates you can upload them here'
          )}
        </div>
        <form
          onSubmit={onSubmitPPPrevPeriod}
          className={classNames({
            'pt-3 border-top': fileToUploadPP,
          })}
        >
          {fileToUploadPP && (
            <ManualUploadFile
              onDeleteFile={deletePPFileToUpload}
              file={fileToUploadPP}
              disabled={false}
              className='mb-3'
            />
          )}
          {fileToUploadPP ? (
            <Button
              className='py-2 ps-3 pe-4 d-flex align-items-center mt-3'
              type='submit'
              disabled={isUploadingPP}
            >
              {isUploadingPP ? (
                <MiniLoader className='MiniLoader--sm d-flex align-items-center justify-content-center me-2 MiniLoader--gray' />
              ) : (
                <SendIcon className='me-2' />
              )}
              <span>{t('Send')}</span>
            </Button>
          ) : (
            <div
              className='row row-cols-auto gx-3 align-items-center'
              style={{
                paddingTop: 30,
              }}
            >
              <div className='col'>
                <FileUploader
                  onSelect={onSelectPPFile}
                  name='file'
                  accept={ACCEPTED_EXTS}
                  className='cursor-pointer Button--outline ps-3 pe-4 py-2 d-flex align-items-center'
                  multiple={false}
                >
                  <>
                    <UploadIcon className='me-2' />
                    {t('Select file')}
                  </>
                </FileUploader>
              </div>
              <div className='col Text--gray Text--sm'>
                File format: CSV, XLS, XLSX
              </div>
            </div>
          )}
        </form>
      </div>
    );
  };

  return (
    <div
      className={classNames('Paper p-4 position-relative overflow-hidden', {
        'mx-auto': !!countPP,
        'mb-3': !countPP,
      })}
    >
      {today.getDate() >= 5 && !countPP && (
        <TipIcon
          className='position-absolute'
          style={{
            right: 16,
            top: 12,
          }}
        />
      )}
      {(today.getDate() >= 5 || isProcessing) && (
        <TopProgressLoader
          isLoading={isProcessing}
          backgroundColor={!!countPP ? '#FF7800' : undefined}
        />
      )}
      {isLoadingPP && <Loader />}
      {renderContent()}
    </div>
  );
}
